// ============================================================
// ANSICHTSSACHE-AGENT – Rahmen einer Listenseite
// ============================================================
// Der Aufbau, den JEDE Listenseite hat. Nicht als Empfehlung,
// sondern als Bauteil — wer eine Liste baut, setzt diese drei
// Stuecke ineinander und bekommt denselben Aufbau wie alle
// anderen:
//
//   <Listenseite>                 so hoch wie das Bild
//     <PageHeader … />            Titel, Zahlen, Aktionen
//     <Listenblatt>               EINE Karte, scrollt innen
//       <Filterkopf … />          zwei Zeilen, feste Kante
//       <Listenkoerper>           der Inhalt, rollt
//       <Listenfuss>              die Summe, klebt unten
//
// WARUM DIE SEITE NICHT SCROLLT, sondern die Liste darin: unten
// steht die Summe der Auswahl, und die ist nur etwas wert, wenn
// man sie IMMER sieht. Eine Seite, die als Ganzes rollt, schiebt
// genau die Zeile aus dem Bild, wegen der man gefiltert hat.
//
// Die Rechnung zur Hoehe: oben die Kopfleiste (`--ans-topbar`),
// darunter beginnt der Inhalt mit 24 Pixeln Luft (`py-6` des
// Rahmens), unten sollen 12 bleiben — dieselben wie unter der
// Navigationsleiste. Also `100dvh - Kopfleiste - 36`, und das
// negative `-mb-3` zieht die untere Luft von 24 auf 12 zurueck,
// damit die Seite keinen Rollbalken bekommt, den niemand
// braucht. Unter `lg` bleibt alles im normalen Fluss: dort ist
// der Bildausschnitt zu klein, um etwas festzuhalten.
// ============================================================

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export function Listenseite({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 lg:-mb-3 lg:h-[calc(100dvh-var(--ans-topbar)-36px)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Die eine Karte, in der die Liste steckt. */
export function Listenblatt({ children }: { children: React.ReactNode }) {
  return (
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">{children}</CardContent>
    </Card>
  );
}

/**
 * Der rollende Teil.
 *
 * Er ist der Rollbereich, an dem Spaltenkopf und Summenzeile
 * kleben (`sticky`). Sein Grund ist `bg-secondary`, der Inhalt
 * darin weiss: den Streifen rechts, den ein Rollbalken
 * freilaesst, der Platz braucht, fuellt sonst die weisse Karte —
 * und neben der grauen Summenzeile blitzt er durch.
 *
 * `relative` ist kein Zierrat: ein absolut gesetztes Element
 * (`sr-only` am Statuszeichen, Fuellflaechen) wird von einem
 * Rollbereich nur dann eingefangen, wenn der sein Bezugskasten
 * ist. Ohne das haengen sich die Texte fuer Vorleseprogramme aus
 * Zeile 40 an das DOKUMENT — und das ganze Fenster rollt eine
 * leere Flaeche unter der Liste hervor (Projekte, 24.09.2026).
 */
export function Listenkoerper({
  children,
  className,
  style,
  ref,
}: {
  children: React.ReactNode;
  className?: string;
  /** Fuer Werte aus Daten — die Hausfarben im Brand-Portal. */
  style?: React.CSSProperties;
  /** Fuer Ansichten, die selbst rollen (die Zeitleiste der Kapazitaet). */
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      className={cn("bg-bg-secondary relative min-h-0 flex-1 overflow-auto", className)}
      style={style}
    >
      {children}
    </div>
  );
}

/**
 * Die Klassen der Summenzeile.
 *
 * Als Klassen und nicht als Bauteil, weil eine Tabelle sie auf
 * ihre `td` legt (nur so steht die Summe unter der Spalte, die
 * sie summiert) und eine Liste ohne Tabelle auf ein `div`.
 * `sticky` sitzt dabei auf den ZELLEN und nicht auf dem `tfoot`:
 * Browser behandeln das Klebenlassen ganzer Tabellenabschnitte
 * unterschiedlich, die Zellen aber einheitlich.
 */
export const LISTENFUSS = "bg-bg-secondary border-border sticky bottom-0 z-10 border-t px-5 py-3";

/** Die Summenzeile fuer Listen ohne Tabelle. */
export function Listenfuss({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(LISTENFUSS, "flex flex-wrap items-center gap-x-6 gap-y-1", className)}>
      {children}
    </div>
  );
}

/**
 * Die linke Haelfte der Summenzeile — „Summe · September 2026"
 * und darunter die Nebenrechnung.
 */
export function Fusstext({ titel, zusatz }: { titel: React.ReactNode; zusatz?: React.ReactNode }) {
  return (
    <span className="min-w-0">
      <span className="text-body-sm text-text-primary font-medium">{titel}</span>
      {zusatz && <span className="text-caption text-text-tertiary ml-2">{zusatz}</span>}
    </span>
  );
}
